import {useMemo} from "react";

import {PermissionsInfo, useUserAttributes} from "@khan/shared-core";

import {RegionOverrideSource, type RegionOverrideResult} from "./types.ts";

const unknownResult = {
    status: "success",
    regionCode: "ZZ",
    regionSource: RegionOverrideSource.USER_SETTING,
} as RegionOverrideResult;

// sync-start:school-location 1080174498 https://github.com/Khan/webapp/blob/master/services/coaches/models/location_property.go
// Implements the region override precedence logic from ADR #616 via user-profile region, district, and U.S. school data.
// See use-region-override.ts for more.
// https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/2174386279/ADR+616+Region+Aware+Standards#Decision
export const useUserSettingsRegion = (): RegionOverrideResult => {
    const userAttributes = useUserAttributes();

    const resultFromQuery = useMemo(() => {
        if (!PermissionsInfo.isLoggedIn || !userAttributes) {
            return unknownResult;
        }

        const region = userAttributes.region;
        const isKAD = userAttributes.userDistrictInfos?.[0]?.isKAD;
        const districtRegion =
            userAttributes.userDistrictInfos?.[0]?.district?.region;
        const schoolLocation = userAttributes.schoolAffiliation?.location;

        if (!region && !districtRegion && !schoolLocation) {
            return unknownResult;
        }

        // User's selected region. Higher precedence than the district and school overrides.
        if (region && region !== "ZZ") {
            return {
                regionCode: region,
                regionSource: RegionOverrideSource.USER_SETTING,
                status: "success",
            } as RegionOverrideResult;
        }

        // District
        // The district override has higher precedence
        // than the school override. isKAD indicates a paid district user.
        if (districtRegion && isKAD && districtRegion !== "ZZ") {
            return {
                regionCode: districtRegion,
                regionSource: RegionOverrideSource.DISTRICT,
                status: "success",
            } as RegionOverrideResult;
        }

        // U.S. school
        // We parse schoolAffiliation.location to determine the region code of the user's U.S.-located school.
        // The school data has the lowest precedence of the user-profile region overrides.
        if (schoolLocation && schoolLocation !== "ZZ") {
            const schoolRegionCode = parseSchoolLocation(schoolLocation);
            if (schoolRegionCode === "ZZ") {
                return unknownResult;
            }

            return {
                regionCode: schoolRegionCode,
                regionSource: RegionOverrideSource.SCHOOL,
                status: "success",
            } as RegionOverrideResult;
        }

        // If there is no region setting, associated district, or associated U.S. school,
        // return the unknown region result (region code "ZZ").
        return unknownResult;
    }, [userAttributes]);

    return resultFromQuery;
};

// Parses the location string from the schoolAffiliation field in the user profile,
// returning either a region code representing the user's U.S.-located school or "ZZ"
// if the location string is malformed.
const parseSchoolLocation = (location: string): string => {
    const locationArray = location.split(",");
    if (locationArray.length > 1) {
        // Country is the last element in the comma-separated location string.
        const country = locationArray[locationArray.length - 1].trim();
        // Region is the second to last element.
        const region = locationArray[locationArray.length - 2].trim();

        if (region && country) {
            return `${country}-${region}`;
        }
    }

    return "ZZ";
};
// sync-end:school-location
